import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ChevronDown, ExternalLink, Link2, Image as ImageIcon } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "../../lib/api";
import { inputCls, btnCls, useConfig, MediaPicker } from "./crud";

const label = (t) => <label className="font-mono-tech text-[10px] tracking-widest text-slate-500 block mb-1">{t}</label>;
const displayUrl = (u) => (u || "").replace(/^https?:\/\//, "");

function LinkForm({ initial, config, onClose, onSaved }) {
  const [m, setM] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(false);
  const set = (k) => (e) => setM({ ...m, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    if (!m.name.trim() || !m.url.trim()) { toast.error("Nombre y URL son obligatorios"); return; }
    setSaving(true);
    try {
      const { _id, created_at, updated_at, ...rest } = m;
      const data = { ...rest, order: Number(m.order) || 0 };
      if (_id) await api.put(`/admin/content/links/${_id}`, { data });
      else await api.post("/admin/content/links", { data });
      toast.success("Guardado"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md max-h-[85vh] overflow-auto p-6" data-testid="link-form-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-sm font-semibold">{m._id ? "Editar enlace" : "Nuevo enlace"}</h3>
          <button onClick={onClose} data-testid="link-form-close"><X size={16} className="text-slate-500" /></button>
        </div>
        <p className="text-xs text-slate-400 mb-5">Modifica el logo, nombre o URL del enlace.</p>
        <div className="space-y-4">
          <div>{label("LOGO")}
            <div className="flex items-center gap-3">
              <span className="w-14 h-14 border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden">
                {m.logo ? <img src={mediaUrl(m.logo)} alt="" className="max-w-full max-h-full object-contain" /> : <Link2 size={18} className="text-slate-300" />}
              </span>
              <div className="space-y-1">
                <button type="button" onClick={() => setPicker(true)} className="inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-1.5 text-xs text-slate-600 hover:border-[#5698C7] hover:text-[#5698C7] transition-colors duration-200" data-testid="link-logo-picker">
                  <ImageIcon size={12} /> Cambiar logo
                </button>
                {m.logo && <button type="button" onClick={() => setM({ ...m, logo: "" })} className="block text-[11px] text-slate-400 hover:text-red-600" data-testid="link-logo-remove">Quitar logo</button>}
              </div>
            </div>
            {picker && <MediaPicker onSelect={(url) => setM({ ...m, logo: url })} onClose={() => setPicker(false)} />}
          </div>
          <div>{label("NOMBRE")}<input className={inputCls} value={m.name} onChange={set("name")} data-testid="link-field-name" /></div>
          <div>{label("URL")}
            <input className={inputCls} placeholder="https://..." value={m.url} onChange={set("url")} data-testid="link-field-url" />
            {m.url && <span className="block mt-1 font-mono-tech text-[10px] text-slate-400 truncate">Se abrirá: {m.url}</span>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>{label("CATEGORÍA")}
              <select className={inputCls} value={m.category} onChange={set("category")} data-testid="link-field-category">
                {(config.link_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>{label("ORDEN")}<input type="number" className={inputCls} value={m.order} onChange={set("order")} data-testid="link-field-order" /></div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            <input type="checkbox" checked={m.visible} onChange={set("visible")} data-testid="link-field-visible" /> Visible
          </label>
        </div>
        <div className="mt-6 flex justify-end"><button onClick={save} disabled={saving} className={btnCls} data-testid="link-save-btn">{saving ? "..." : "GUARDAR CAMBIOS"}</button></div>
      </div>
    </div>
  );
}

function LinkCard({ m, onEdit, onDelete }) {
  return (
    <div className="border border-slate-200 rounded-xl bg-white shadow-sm p-4 flex flex-col items-center text-center hover:shadow-md transition-shadow duration-200" data-testid={`link-card-${m._id}`}>
      <div className="h-16 flex items-center justify-center mb-3">
        {m.logo ? <img src={mediaUrl(m.logo)} alt={m.name} className="max-h-16 max-w-[140px] object-contain" />
          : <span className="w-12 h-12 rounded-lg bg-[#5698C7]/10 text-[#5698C7] flex items-center justify-center"><Link2 size={20} /></span>}
      </div>
      <span className="font-display text-[13px] font-semibold tracking-wide text-[#0B1220] uppercase">{m.name}</span>
      <span className="font-mono-tech text-[10px] text-slate-400 truncate w-full mt-0.5 mb-3">{displayUrl(m.url)}</span>
      <a href={m.url} target="_blank" rel="noreferrer" className={btnCls + " w-full justify-center"} data-testid={`link-open-${m._id}`}>
        <ExternalLink size={13} /> Ir al enlace
      </a>
      <div className="grid grid-cols-2 gap-2 w-full mt-2">
        <button onClick={onEdit} className="inline-flex items-center justify-center gap-1.5 border border-slate-300 rounded-md px-3 py-1.5 text-[11px] text-slate-600 hover:border-[#5698C7] hover:text-[#5698C7] transition-colors duration-200" data-testid={`link-edit-${m._id}`}>
          <Pencil size={11} /> Editar
        </button>
        <button onClick={onDelete} className="inline-flex items-center justify-center gap-1.5 border border-slate-300 rounded-md px-3 py-1.5 text-[11px] text-slate-600 hover:border-red-400 hover:text-red-600 transition-colors duration-200" data-testid={`link-delete-${m._id}`}>
          <Trash2 size={11} /> Eliminar
        </button>
      </div>
      {m.visible === false && <span className="mt-2 font-mono-tech text-[9px] tracking-widest text-amber-600">OCULTO</span>}
    </div>
  );
}

export default function LinksAdmin() {
  const config = useConfig();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [closed, setClosed] = useState({});

  const load = () => api.get("/admin/content/links").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const cats = config?.link_categories || [];
    const known = cats.map((cat) => ({ cat, mods: items.filter((i) => i.category === cat) }));
    const other = items.filter((i) => !cats.includes(i.category));
    if (other.length) known.push({ cat: "Otros", mods: other });
    return known.filter((g) => g.mods.length);
  }, [items, config]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este enlace?")) return;
    await api.delete(`/admin/content/links/${id}`);
    toast.success("Eliminado"); load();
  };

  const blank = () => ({ name: "", logo: "", url: "", category: (config?.link_categories || [])[0] || "",
    icon: "link", description: { es: "", en: "" }, order: items.length, visible: true });

  if (!config) return null;

  return (
    <div data-testid="links-admin">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-xl font-light">Enlaces Corporativos</h1>
        <button onClick={() => setEditing(blank())} className={btnCls} data-testid="links-add-btn"><Plus size={14} /> AÑADIR</button>
      </div>
      <p className="text-xs text-slate-400 mb-6">Accesos rápidos a las webs que utilizas cada día, clasificados por categoría.</p>

      {grouped.length === 0 && <p className="text-xs text-slate-400">Sin enlaces. Añade el primero con AÑADIR.</p>}
      {grouped.map(({ cat, mods }) => (
        <div key={cat} className="mb-6">
          <button onClick={() => setClosed({ ...closed, [cat]: !closed[cat] })} className="w-full flex items-center gap-2 mb-3 text-left" data-testid={`links-cat-toggle-${cat}`}>
            <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${closed[cat] ? "-rotate-90" : ""}`} />
            <span className="font-mono-tech text-[10px] tracking-widest text-slate-500">{cat.toUpperCase()}</span>
            <span className="font-mono-tech text-[10px] text-slate-300">({mods.length})</span>
            <span className="flex-1 border-t border-slate-200 ml-2" />
          </button>
          {!closed[cat] && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {mods.map((m) => <LinkCard key={m._id} m={m} onEdit={() => setEditing({ ...m })} onDelete={() => remove(m._id)} />)}
            </div>
          )}
        </div>
      ))}
      {editing && <LinkForm initial={editing} config={config} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
